'use client';

import { Button, Modal } from '@common/components';
import { useToast } from '@common/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { AuthMutation } from '@/api';
import { BREAKPOINTS } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';

import { converter } from '../_utils/converter';

interface DonationReservationModalProps {
  opened: boolean;
  businessId?: number;
  productNumber?: string;
  onSuccess?: () => void;
  onClose?: () => void;
  description?: string;
}

/**
 * * 기부 사전예약 임시 모달
 */
export const DonationReservationModal = ({
  opened,
  businessId,
  productNumber,
  onSuccess,
  onClose,
  description,
}: DonationReservationModalProps) => {
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const { toastError } = useToast();
  const queryClient = useQueryClient();

  const { mutate: createReservation, isPending: isCreatingReservation } =
    useMutation({
      ...AuthMutation['post:/wello/v2/auth/home/donation/reservation']({
        body: {
          homeDonationBusinessId: businessId,
          productNumber: productNumber,
        },
      }),
      onSuccess: () => {
        onSuccess?.();
        queryClient.invalidateQueries();
      },
      onError: (error) => {
        toastError({ error });
      },
    });

  const safeHighlightedDescription = converter.highlight(
    converter.sanitizeHTML(description ?? ''),
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      className="max-h-[90%] min-w-240 p-0 lg:max-h-[90%] lg:min-w-240 lg:p-0"
      loading={isCreatingReservation}
    >
      <div className="flex flex-col items-center px-20 pb-24 pt-32">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="48"
          height="48"
          viewBox="0 0 48 48"
          fill="none"
        >
          <path
            opacity="0.4"
            d="M40 16V24.9219C40 25.3339 39.604 25.602 39.21 25.48C38.04 25.116 36.792 24.9562 35.478 25.0142C29.908 25.2602 25.2558 29.9119 25.0098 35.4819C24.9518 36.7959 25.1121 38.0439 25.4761 39.2139C25.5981 39.6059 25.33 40.0039 24.918 40.0039H10C6 40.0039 4 38.0039 4 34.0039V16.0039H40V16Z"
            fill="#33A6FF"
          />
          <path
            d="M34 7H31.5V4C31.5 3.172 30.828 2.5 30 2.5C29.172 2.5 28.5 3.172 28.5 4V7H15.5V4C15.5 3.172 14.828 2.5 14 2.5C13.172 2.5 12.5 3.172 12.5 4V7H10C6 7 4 9 4 13V16H40V13C40 9 38 7 34 7Z"
            fill="#33A6FF"
          />
          <path
            d="M14.0396 26C12.9356 26 12.0293 25.104 12.0293 24C12.0293 22.896 12.9155 22 14.0195 22H14.0396C15.1456 22 16.0396 22.896 16.0396 24C16.0396 25.104 15.1436 26 14.0396 26ZM24.0396 24C24.0396 22.896 23.1456 22 22.0396 22H22.0195C20.9155 22 20.0293 22.896 20.0293 24C20.0293 25.104 20.9356 26 22.0396 26C23.1436 26 24.0396 25.104 24.0396 24ZM16.0396 32C16.0396 30.896 15.1456 30 14.0396 30H14.0195C12.9155 30 12.0293 30.896 12.0293 32C12.0293 33.104 12.9356 34 14.0396 34C15.1436 34 16.0396 33.104 16.0396 32Z"
            fill="#33A6FF"
          />
          <path
            d="M36 28C31.582 28 28 31.582 28 36C28 40.418 31.582 44 36 44C40.418 44 44 40.418 44 36C44 31.582 40.418 28 36 28ZM38.8501 38.8481C38.6541 39.0441 38.3981 39.1401 38.1421 39.1401C37.8861 39.1401 37.6301 39.0421 37.4341 38.8481L35.292 36.7061C35.104 36.5181 35 36.264 35 35.998V32.998C35 32.446 35.448 31.998 36 31.998C36.552 31.998 37 32.446 37 32.998V35.584L38.8501 37.4321C39.2401 37.8261 39.2401 38.4581 38.8501 38.8481Z"
            fill="#33A6FF"
          />
        </svg>

        <p className="py-8 font-700 text-Gray-900 typo-body2">
          기부 사전 예약 안내
        </p>

        <p
          className="pb-8 font-400 text-Gray-700 typo-body3"
          dangerouslySetInnerHTML={{
            __html: safeHighlightedDescription,
          }}
        />

        <br />

        <p className="pb-8 font-400 text-Gray-700 typo-caption1">
          사전 예약을 해주시면,
          <br />
          기부 서비스 재개 시 알림톡을 발송 드립니다.
          <br />
          이벤트 및 세액 공제 혜택은
          <br />
          기부 시점을 기준으로 적용됩니다.
        </p>

        <Button
          theme="primary-fill"
          dimensions={isDesktopViewport ? 'H56' : 'H48'}
          className="mt-16 w-full"
          onClick={() =>
            createReservation({
              body: {
                homeDonationBusinessId: businessId,
                productNumber: productNumber,
              },
            })
          }
          loading={isCreatingReservation}
        >
          사전 예약 신청하기
        </Button>
      </div>
    </Modal>
  );
};
